import React, { useEffect, useState } from "react";
import { Printer, TrendingUp, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";
import { tk, bn, fmtDate, todayStr } from "../lib/format";
import { useSettings } from "../context/SettingsContext";
import { PageHeader, Loader, Empty } from "../components/kit";

function Cell({ label, value, tone }: { label: string; value: string; tone?: "green" | "red" | "blue" }) {
  const c = tone === "green" ? "text-emerald-700" : tone === "red" ? "text-red-600" : tone === "blue" ? "text-blue-700" : "text-slate-900";
  return (
    <div className="border border-slate-200 rounded-lg px-4 py-3">
      <p className="text-xs text-slate-500">{label}</p>
      <p className={`num mt-0.5 text-xl font-bold ${c}`}>{value}</p>
    </div>
  );
}

export default function ProfitReport() {
  const { settings } = useSettings();
  const [mode, setMode] = useState<"month" | "range">("month");
  const [month, setMonth] = useState(() => todayStr().slice(0, 7));
  const [start, setStart] = useState(todayStr());
  const [end, setEnd] = useState(todayStr());
  const [data, setData] = useState<any>(null);

  const load = () => {
    setData(null);
    const params = mode === "month" ? { month } : { start, end };
    api.get("/reports/profit", { params }).then((r) => setData(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [mode, month, start, end]);

  return (
    <div data-testid="profit-report-page">
      <PageHeader
        title="লাভ / ক্ষতি রিপোর্ট"
        subtitle="বিক্রয় মূল্য ও ক্রয়মূল্যের পার্থক্য থেকে প্রকৃত লাভ"
        actions={<button className="btn btn-primary btn-sm no-print" onClick={() => window.print()} data-testid="profit-print-button"><Printer size={16} /> প্রিন্ট / PDF</button>}
      />

      <div className="card p-4 flex flex-wrap items-center gap-3 mb-4 no-print">
        <select className="inp w-40" value={mode} onChange={(e) => setMode(e.target.value as any)} data-testid="profit-mode-select">
          <option value="month">মাস অনুযায়ী</option>
          <option value="range">তারিখ পরিসীমা</option>
        </select>
        {mode === "month" ? (
          <input type="month" className="inp w-48" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="profit-month-input" />
        ) : (
          <div className="flex items-center gap-2">
            <input type="date" className="inp" value={start} onChange={(e) => setStart(e.target.value)} data-testid="profit-start-input" />
            <span className="text-slate-400">—</span>
            <input type="date" className="inp" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="profit-end-input" />
          </div>
        )}
      </div>

      {!data ? <Loader /> : (
        <div className="print-area bg-white rounded-xl border border-slate-200 p-5 md:p-6" style={{ fontFamily: "'Hind Siliguri', sans-serif" }}>
          <div className="hidden print:flex items-center justify-between border-b-2 border-slate-800 pb-3 mb-4">
            <div><h1 className="text-xl font-extrabold">{settings.shop.name}</h1><p className="text-sm text-slate-500">{settings.shop.address}</p></div>
            <p className="font-bold text-emerald-700">লাভ / ক্ষতি রিপোর্ট</p>
          </div>

          <div className="flex items-center gap-2 mb-4 text-slate-700">
            <TrendingUp size={18} className="text-emerald-600" />
            <span className="font-semibold">{mode === "month" ? bn(month) : `${fmtDate(start)} — ${fmtDate(end)}`}</span>
            <span className="text-sm text-slate-400">• {bn(data.memo_count)} টি মেমো</span>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="profit-summary">
            <Cell label="মোট বিক্রি" value={tk(data.total_sale)} tone="blue" />
            <Cell label="পণ্যের ক্রয়মূল্য" value={tk(data.total_cost)} />
            <Cell label="মোট লাভ (গ্রস)" value={tk(data.gross_profit)} tone="green" />
            <Cell label="দোকান খরচ" value={tk(data.total_expense)} tone="red" />
            <Cell label="প্রকৃত লাভ (নিট)" value={tk(data.net_profit)} tone={data.net_profit >= 0 ? "green" : "red"} />
          </div>

          {data.uncosted_items > 0 && (
            <div className="mt-4 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800" data-testid="profit-warning">
              <AlertTriangle size={18} className="shrink-0 mt-0.5" />
              <span>{bn(data.uncosted_items)} টি আইটেমের ক্রয়মূল্য জানা নেই (বিক্রি {tk(data.uncosted_sale)})। এগুলোর লাভ সম্পূর্ণ ধরা হয়েছে — সঠিক লাভ পেতে পণ্যের ক্রয় দর যোগ করুন।</span>
            </div>
          )}

          <h2 className="font-bold text-slate-800 mt-6 mb-2">মেমো ভিত্তিক লাভ</h2>
          {data.memos.length === 0 ? <Empty text="এই সময়ে কোনো বিক্রি নেই" /> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-slate-200" data-testid="profit-table">
                <thead><tr className="bg-slate-100 text-left"><th className="px-3 py-2">তারিখ</th><th className="px-3 py-2">মেমো</th><th className="px-3 py-2">ক্রেতা</th><th className="px-3 py-2 text-right">বিক্রি</th><th className="px-3 py-2 text-right">ক্রয়মূল্য</th><th className="px-3 py-2 text-right">লাভ</th></tr></thead>
                <tbody>
                  {data.memos.map((m: any) => (
                    <tr key={m.id} className="border-t border-slate-100" data-testid={`profit-row-${m.invoice_no}`}>
                      <td className="px-3 py-1.5 num">{fmtDate(m.date)}</td>
                      <td className="px-3 py-1.5 font-semibold">{m.invoice_no}{m.uncosted > 0 && <span className="text-amber-500">*</span>}</td>
                      <td className="px-3 py-1.5">{m.customer}</td>
                      <td className="px-3 py-1.5 text-right num">{tk(m.sale)}</td>
                      <td className="px-3 py-1.5 text-right num text-slate-500">{tk(m.cost)}</td>
                      <td className="px-3 py-1.5 text-right num font-bold text-emerald-700">{tk(m.profit)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
